import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrders } from '../contexts/OrderContext';
import { Card } from '../components/ui/Card';
import { StatusBadge } from '../components/ui/StatusBadge';
import { BarChart3, Users, AlertCircle, Truck, PlusCircle } from 'lucide-react';
import { ZONES } from '../constants/zones';
import { Button } from '../components/ui/Button';
export function AdminDashboard() {
  const {
    orders,
    tours
  } = useOrders();
  const navigate = useNavigate();
  // Stats
  const totalOrders = orders.length;
  const activeTours = tours.filter(t => t.status === 'EN_COURS').length;
  const lateOrders = orders.filter(o => {
    const delivery = new Date(o.estimatedDeliveryDate);
    const today = new Date();
    return delivery < today && o.status !== 'LIVREE';
  }).length;
  // Orders by Zone
  const ordersByZone = ZONES.map(zone => ({
    zone,
    count: orders.filter(o => o.zone === zone && o.status !== 'LIVREE').length
  })).sort((a, b) => b.count - a.count).slice(0, 5);
  return <div className="space-y-6" data-id="element-229">
      <div className="flex justify-between items-center" data-id="element-230">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-231">
          Vue d'ensemble Admin
        </h1>
        <Button onClick={() => navigate('/orders/new')} leftIcon={<PlusCircle className="w-4 h-4" data-id="element-233" />} data-id="element-232">
          Nouvelle Commande
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-234">
        <Card className="bg-white" data-id="element-235">
          <div className="flex items-center" data-id="element-236">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-4" data-id="element-237">
              <BarChart3 className="w-6 h-6" data-id="element-238" />
            </div>
            <div data-id="element-239">
              <p className="text-sm text-gray-500" data-id="element-240">Total Commandes</p>
              <p className="text-2xl font-bold" data-id="element-241">{totalOrders}</p>
            </div>
          </div>
        </Card>

        <Card className="bg-white" data-id="element-242">
          <div className="flex items-center" data-id="element-243">
            <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4" data-id="element-244">
              <Truck className="w-6 h-6" data-id="element-245" />
            </div>
            <div data-id="element-246">
              <p className="text-sm text-gray-500" data-id="element-247">Tournées Actives</p>
              <p className="text-2xl font-bold" data-id="element-248">{activeTours}</p>
            </div>
          </div>
        </Card>

        <Card className="bg-white" data-id="element-249">
          <div className="flex items-center" data-id="element-250">
            <div className="p-3 rounded-full bg-red-100 text-red-600 mr-4" data-id="element-251">
              <AlertCircle className="w-6 h-6" data-id="element-252" />
            </div>
            <div data-id="element-253">
              <p className="text-sm text-gray-500" data-id="element-254">En Retard</p>
              <p className="text-2xl font-bold" data-id="element-255">{lateOrders}</p>
            </div>
          </div>
        </Card>

        <Card className="bg-white" data-id="element-256">
          <div className="flex items-center" data-id="element-257">
            <div className="p-3 rounded-full bg-purple-100 text-purple-600 mr-4" data-id="element-258">
              <Users className="w-6 h-6" data-id="element-259" />
            </div>
            <div data-id="element-260">
              <p className="text-sm text-gray-500" data-id="element-261">Zones Actives</p>
              <p className="text-2xl font-bold" data-id="element-262">
                {ordersByZone.filter(z => z.count > 0).length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-263">
        {/* Zone Distribution */}
        <Card title="Commandes par Zone (Top 5)" data-id="element-264">
          <div className="space-y-4" data-id="element-265">
            {ordersByZone.map(item => <div key={item.zone} className="flex items-center" data-id="element-266">
                <div className="w-32 text-sm text-gray-600 truncate" data-id="element-267">
                  {item.zone}
                </div>
                <div className="flex-1 mx-3" data-id="element-268">
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-269">
                    <div className="h-full bg-blue-500 rounded-full" style={{
                  width: `${item.count / totalOrders * 100}%`
                }} data-id="element-270" />
                  </div>
                </div>
                <div className="text-sm font-bold text-gray-900" data-id="element-271">
                  {item.count}
                </div>
              </div>)}
          </div>
        </Card>

        {/* Recent Orders Table */}
        <Card title="Dernières Commandes" noPadding data-id="element-272">
          <div className="overflow-x-auto" data-id="element-273">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-274">
              <thead className="bg-gray-50" data-id="element-275">
                <tr data-id="element-276">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-277">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-278">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-279">
                    Statut
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-280">
                {orders.slice(0, 5).map(order => <tr key={order.id} data-id="element-281">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-282">
                      {order.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-283">
                      {order.clientName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-284">
                      <StatusBadge status={order.status} data-id="element-285" />
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>;
}